import { todayAsOf } from '@brainfart/shared';
import React, { Component, Suspense, lazy, useEffect, useState, type ReactNode } from 'react';
import ReactDOM from 'react-dom/client';
import { LazyMotion, MotionGlobalConfig } from 'framer-motion';
import { App } from './App';
import { StatsScreen } from './components/StatsScreen';
import { LoadingSpinner } from './components/LoadingSpinner';
import { InstallSuccess } from './components/InstallSheet';
import { ReportQuestionHost } from './components/ReportQuestion';
import { LevelUpHost } from './components/LevelUp';
import { AchievementToastHost } from './components/AchievementToast';
import { initAchievements } from './achievements';
import './index.css';
import { isStandalone } from './installPrompt';
import { trackAppOpened } from './telemetry';
import { loadOrReload } from './chunkReload';

trackAppOpened(isStandalone(), new URLSearchParams(window.location.search).get('source'));
initAchievements();

if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {});
  });
}

// Daily, Solo and Duel pick their questions on the device, so they need the
// whole question bank (~60% of the app). They load on demand, and are
// prefetched once the homepage is idle, so the first screen paints fast on
// phone data and tapping into a mode is still instant.
const loadSolo = () => import('./SoloApp');
const loadDaily = () => import('./DailyApp');
const loadDuel = () => import('./DuelApp');
const SoloApp = lazy(loadOrReload(() => loadSolo().then(mod => ({ default: mod.SoloApp }))));
const DailyApp = lazy(loadOrReload(() => loadDaily().then(mod => ({ default: mod.DailyApp }))));
const DuelApp = lazy(loadOrReload(() => loadDuel().then(mod => ({ default: mod.DuelApp }))));
// The phone tests (e2e/) set this flag before the page loads: animations then
// jump to their end state, so screens are readable on the tests' frozen clock.
if ((window as { __BF_E2E__?: boolean }).__BF_E2E__) MotionGlobalConfig.skipAnimations = true;

const loadMotionFeatures = loadOrReload(() => import('./motionFeatures').then(f => f.default));

window.addEventListener('load', () => {
  const prefetch = () => { loadDaily().catch(() => {}); loadSolo().catch(() => {}); loadDuel().catch(() => {}); };
  if ('requestIdleCallback' in window) window.requestIdleCallback(prefetch, { timeout: 4000 });
  else setTimeout(prefetch, 1500);
});

type View = 'multiplayer' | 'solo' | 'daily' | 'stats' | 'duel';

function initialView(): View {
  const params = new URLSearchParams(window.location.search);
  if (params.has('daily')) return 'daily';
  if (params.has('duel')) return 'duel';
  if (params.has('stats')) return 'stats';
  if (params.has('solo')) return 'solo';
  return 'multiplayer';
}

function Root() {
  const [view, setView] = useState<View>(initialView);
  // A new screen starts at the top, not wherever the last one was scrolled to.
  useEffect(() => { window.scrollTo(0, 0); }, [view]);

  const enterSolo = () => {
    const seed = Math.floor(Math.random() * 1_000_000_000);
    window.history.replaceState(null, '', `${window.location.pathname}?solo=1&seed=${seed}&b=${todayAsOf()}`);
    setView('solo');
  };

  const enterDaily = () => {
    window.history.replaceState(null, '', `${window.location.pathname}?daily=1`);
    setView('daily');
  };

  // With a category, the duel screen goes straight into matchmaking.
  const enterDuel = (category?: string) => {
    const q = category ? `?duel=1&category=${encodeURIComponent(category)}` : '?duel=1';
    window.history.replaceState(null, '', `${window.location.pathname}${q}`);
    setView('duel');
  };

  const enterStats = () => {
    window.history.replaceState(null, '', `${window.location.pathname}?stats=1`);
    setView('stats');
  };

  const goHome = () => {
    window.history.replaceState(null, '', window.location.pathname);
    setView('multiplayer');
  };

  const screen = (() => {
    switch (view) {
      case 'solo':
        return <SoloApp onExitToMultiplayer={goHome} onPlayDuel={enterDuel} />;
      case 'daily':
        return <DailyApp onHome={goHome} onPlayDuel={enterDuel} />;
      case 'duel':
        return <DuelApp onHome={goHome} />;
      case 'stats':
        return <StatsScreen onHome={goHome} />;
      default:
        return <App onPlaySolo={enterSolo} onPlayDaily={enterDaily} onPlayDuel={enterDuel} onViewStats={enterStats} />;
    }
  })();
  return (
    <ChunkErrorBoundary onHome={goHome}>
      <Suspense fallback={<ScreenLoading />}>{screen}</Suspense>
    </ChunkErrorBoundary>
  );
}

// Last resort when a screen's code can't be fetched (offline, or a deploy
// mid-session that a reload didn't fix): never a blank page.
class ChunkErrorBoundary extends Component<{ children: ReactNode; onHome: () => void }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1rem', padding: '2rem', textAlign: 'center', background: '#0F0D1F' }}>
        <div style={{ fontSize: '3rem' }}>📡</div>
        <p style={{ fontWeight: 700, fontSize: '1.2rem' }}>Couldn't load that screen</p>
        <p style={{ color: '#999' }}>Check your connection and try again.</p>
        <button className="btn btn-primary" onClick={() => window.location.reload()} style={{ padding: '0.9rem 1.5rem' }}>Try again</button>
        <button className="btn btn-ghost" onClick={() => { this.setState({ failed: false }); this.props.onHome(); }}>🏠 Home</button>
      </div>
    );
  }
}

function ScreenLoading() {
  return <LoadingSpinner />;
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    {/* Animations use the lightweight m.* components; `strict` catches any stray motion.* import. */}
    <LazyMotion features={loadMotionFeatures} strict>
      <Root />
      <InstallSuccess />
      <ReportQuestionHost />
      <LevelUpHost />
      <AchievementToastHost />
    </LazyMotion>
  </React.StrictMode>
);
